import { FLAGS } from '@gozoki/feature-flags'
import { GuideButton } from './-guide-button'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { AppContainer } from '~/components/new/app-ui/app-container'
import { AppHeader } from '~/components/new/app-ui/app-header'
import { AppTabs, type AppTab } from '~/components/new/app-ui/app-tabs'
import { Title } from '~/components/new/app-ui/title'
import { SandboxBackButton } from '~/components/routes/sandbox-previsions/sandbox-back-button'
import { TabsContent } from '~/components/ui/tabs'
import { useFeatureFlag } from '~/hooks/useFeatureFlag'
import { FORECAST_REFERENCE_QUERY_OPTIONS } from '~/lib/forecast-reference-queries'
import { api } from '~/lib/tuyau'
import { withRoutePermissions } from '~/utils/rbac'
import { CorrectionsToggle } from '../previsions-produits/-corrections-toggle'
import { PrevisionsStoreSelector } from '../previsions-long-terme/-previsions-store-selector'
import {
  WEEKLY_STRATEGIES,
  WEEKLY_STRATEGY_LABELS,
  weeklySearchSchema,
  type WeeklyStrategy,
} from './-search-schema'
import { WeeklyBacktestView } from './-weekly-backtest'
import { useWeeklyBacktest, useWeeklyForecast, weekRange } from './-weekly-data'
import { WeeklyTable } from './-weekly-table'

const TABS: AppTab<'forecast' | 'backtest'>[] = [
  { value: 'forecast', label: 'Prévision' },
  { value: 'backtest', label: 'Backtest' },
]

export const Route = createFileRoute('/_authenticated/previsions-semaine/')({
  validateSearch: weeklySearchSchema,
  staticData: { permission: ['read', 'previsions.previsions.forecast'] },
  component: WeeklyPrevisionPage,
})

withRoutePermissions(Route)

function WeeklyPrevisionPage() {
  const enabled = useFeatureFlag(FLAGS.uvcForecasting52Weeks)
  const search = Route.useSearch()
  const navigate = Route.useNavigate()

  const setSearch = (next: Partial<typeof search>) =>
    navigate({ search: (prev) => ({ ...prev, ...next }) })

  const { data: stores = [] } = useQuery(
    api.forecast.forecastStores.queryOptions({}, FORECAST_REFERENCE_QUERY_OPTIONS),
  )
  const { data: categories = [] } = useQuery(
    api.forecast.productCategories.queryOptions(
      {},
      { enabled, ...FORECAST_REFERENCE_QUERY_OPTIONS },
    ),
  )
  const forecast = useWeeklyForecast(search, enabled)
  const backtest = useWeeklyBacktest(search, enabled)

  const storeLabels = new Map(stores.map((store) => [store.storeId, store.label]))
  const categoryNames = new Map(categories.map((category) => [category.id, category.name]))

  if (!enabled) {
    return (
      <>
        <AppHeader>
          <div className="flex items-center gap-3">
            <SandboxBackButton />
            <Title>Prévisions par semaine</Title>
          </div>
        </AppHeader>
        <AppContainer>
          <p className="text-muted-foreground">Fonctionnalité non activée.</p>
        </AppContainer>
      </>
    )
  }

  const data = forecast.data

  return (
    <>
      <AppHeader>
        <div className="flex items-center gap-3">
          <SandboxBackButton />
          <Title>Prévisions par semaine</Title>
          <GuideButton />
        </div>
      </AppHeader>
      <AppContainer>
        <div className="flex flex-wrap items-center gap-3">
          {search.tab === 'forecast' && (
            <PrevisionsStoreSelector
              selectedIds={search.storeIds}
              onApply={(storeIds) => setSearch({ storeIds })}
              placeholder="Sélectionner des boutiques"
              title="Boutiques à prévoir"
            />
          )}

          {search.tab === 'forecast' && (
            <label className="text-muted-foreground flex items-center gap-2 text-sm">
              Stratégie
              <select
                value={search.strategy}
                onChange={(event) => setSearch({ strategy: event.target.value as WeeklyStrategy })}
                className="border-input h-9 rounded-md border bg-transparent px-2"
              >
                {WEEKLY_STRATEGIES.map((strategy) => (
                  <option key={strategy} value={strategy}>
                    {WEEKLY_STRATEGY_LABELS[strategy]}
                  </option>
                ))}
              </select>
            </label>
          )}

          <CorrectionsToggle
            checked={search.corrections}
            onChange={(corrections) => setSearch({ corrections })}
          />

          <label className="text-muted-foreground flex items-center gap-2 text-sm">
            Semaines
            <input
              type="number"
              min={1}
              max={52}
              value={search.weeks}
              onChange={(event) => setSearch({ weeks: Number(event.target.value) })}
              className="border-input h-9 w-20 rounded-md border bg-transparent px-2 tabular-nums"
            />
          </label>
        </div>

        <AppTabs
          className="mt-4"
          tabs={TABS}
          value={search.tab}
          onTabChange={(tab) => setSearch({ tab })}
        >
          <TabsContent value="forecast">
            {search.storeIds.length === 0 && (
              <p className="text-muted-foreground">
                Sélectionne au moins une boutique pour afficher la prévision.
              </p>
            )}

            {forecast.isPending && search.storeIds.length > 0 && (
              <p className="text-muted-foreground">Calcul en cours…</p>
            )}

            {data !== undefined && (
              <>
                <p className="text-muted-foreground text-sm">
                  Ancre : semaine du {weekRange(data.anchorWeek)} — moteur {data.algorithmVersion}
                </p>
                <div className="mt-4 flex flex-col gap-6">
                  {data.stores.map((store) => (
                    <WeeklyTable
                      key={store.storeId}
                      forecast={data}
                      store={store}
                      storeLabel={storeLabels.get(store.storeId) ?? `Boutique ${store.storeId}`}
                      categoryNames={categoryNames}
                    />
                  ))}
                </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="backtest">
            {backtest.isPending && <p className="text-muted-foreground">Mesure en cours…</p>}
            {backtest.isError && (
              <p className="text-amber-600">Erreur pendant la mesure. Réessaie.</p>
            )}
            {backtest.data !== undefined && <WeeklyBacktestView data={backtest.data} />}
          </TabsContent>
        </AppTabs>
      </AppContainer>
    </>
  )
}
